import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const clock = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "am" : "pm"}`;

/** The LimoLinkX blog on a timer: two posts a day, topic, photograph and all. */
export const BlogAutoCard = ({ onWritten }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blog/auto");
      setState(data);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (changes) => {
    const next = { ...state, ...changes };
    setState(next);
    setBusy("save"); setError(""); setNotice("");
    try {
      const { data } = await api.put("/admin/blog/auto", {
        enabled: next.enabled, hours: next.hours, audience: next.audience, themes: next.themes });
      setState(data);
      setNotice(data.enabled
        ? `On — ${data.hours.length} post${data.hours.length === 1 ? "" : "s"} a day, written and published for you.`
        : "Off — nothing will be written until you switch it back on.");
    } catch (e) { setError(errMsg(e)); load(); } finally { setBusy(""); }
  };

  const setHour = (index, value) => {
    const hours = [...(state.hours || [])];
    hours[index] = Number(value);
    save({ hours });
  };

  const writeNow = async () => {
    setBusy("now"); setError(""); setNotice("");
    try {
      await api.post("/admin/blog/auto/run");
      setNotice("Writing one now — it takes about a minute. This panel refreshes itself.");
      setTimeout(() => { load(); onWritten?.(); }, 45000);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!state) return null;
  const hours = state.hours || [];

  return (
    <div data-testid="blog-auto-card" className="rounded-xl border border-ink-600 bg-ink-800 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Daily writer
          </p>
          <p className="mt-1 text-sm text-slate-300">Two posts a day, on their own</p>
        </div>
        <Switch data-testid="blog-auto-toggle" checked={!!state.enabled}
          disabled={busy === "save"} onCheckedChange={(v) => save({ enabled: v })} />
      </div>

      <div className="mt-3 space-y-2">
        {[0, 1].map((i) => (
          <label key={i} className="flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Post {i + 1}
            </span>
            <select data-testid={`blog-auto-hour-${i}`} value={hours[i] ?? (i ? 15 : 9)}
              disabled={!state.enabled || busy === "save"}
              onChange={(e) => setHour(i, e.target.value)}
              className="h-9 rounded-lg border border-ink-600 bg-ink-900 px-2 text-sm text-slate-200 disabled:opacity-50">
              {HOURS.map((h) => <option key={h} value={h}>{clock(h)}</option>)}
            </select>
          </label>
        ))}
        <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-600">
          <CalendarClock className="h-3 w-3" /> Central time
        </p>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        The AI picks its own subject each time — it reads what it has already written so it never
        repeats itself — then writes the post, shoots a cover photo and publishes it to
        limolinkx.com/blog.
      </p>

      <Button data-testid="blog-auto-run" size="sm" variant="outline" className="mt-3 w-full"
        disabled={busy === "now"} onClick={writeNow}>
        {busy === "now" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          : <Sparkles className="mr-2 h-3.5 w-3.5" />}
        Write one now
      </Button>

      {state.runs?.length > 0 && (
        <ul data-testid="blog-auto-runs" className="mt-3 space-y-1 border-t border-ink-600 pt-3">
          {state.runs.slice(0, 4).map((r) => (
            <li key={r.slot} className="truncate text-[11px] text-slate-500">
              <span className={r.status === "failed" ? "text-red-400" : "text-gold/70"}>
                {r.status === "failed" ? "failed" : r.status === "writing" ? "writing" : "published"}
              </span>
              {" · "}{r.title || r.error || r.slot}
            </li>
          ))}
        </ul>
      )}
      {notice && <p data-testid="blog-auto-notice" className="mt-2 text-xs text-gold">{notice}</p>}
      {error && <p data-testid="blog-auto-error" className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
};
